<template>
  <div class="profile-page">
    <PageHeader
      title="Account"
      subtitle="Security, credentials, and billing for your ScrapingAnt account."
    />

    <v-row>
      <!-- Left column -->
      <v-col cols="12" lg="6">
        <SectionCard
          title="Account security"
          subtitle="API token and password management."
          class="mb-6"
        >
          <CredentialField
            label="API token"
            :value="api_token || ''"
            secret
          />
          <div class="profile-page__regen">
            <v-btn
              text
              small
              color="primary"
              class="profile-page__btn"
              @click="regenerateApiToken"
            >
              <v-icon left small>mdi-refresh</v-icon>Regenerate token
            </v-btn>
          </div>

          <div class="profile-page__divider"></div>

          <h3 class="profile-page__subhead">Change password</h3>
          <v-text-field
            v-model="$v.change_password.old_password.$model"
            :type="showOldPassword ? 'text' : 'password'"
            :error-messages="oldPasswordErrors"
            :append-icon="showOldPassword ? 'mdi-eye' : 'mdi-eye-off'"
            @input="$v.change_password.old_password.$touch()"
            @click:append="showOldPassword = !showOldPassword"
            label="Old password"
            outlined
            dense
            class="mb-2"
          ></v-text-field>
          <v-text-field
            v-model="$v.change_password.new_password.$model"
            :type="showNewPassword ? 'text' : 'password'"
            :error-messages="newPasswordErrors"
            :append-icon="showNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
            @input="$v.change_password.new_password.$touch()"
            @click:append="showNewPassword = !showNewPassword"
            label="New password"
            outlined
            dense
            class="mb-2"
          ></v-text-field>
          <v-text-field
            v-model="$v.change_password.new_password_repeated.$model"
            :type="showPasswordOneMoreTime ? 'text' : 'password'"
            :error-messages="OneMoreTimePasswordErrors"
            :append-icon="showPasswordOneMoreTime ? 'mdi-eye' : 'mdi-eye-off'"
            @input="$v.change_password.new_password_repeated.$touch()"
            @blur="$v.change_password.new_password_repeated.$touch()"
            @click:append="showPasswordOneMoreTime = !showPasswordOneMoreTime"
            label="Confirm new password"
            outlined
            dense
          ></v-text-field>
          <template #actions>
            <v-btn
              color="primary"
              depressed
              :disabled="isChangePasswordDisabled"
              @click="changeUserPassword"
            >Change password</v-btn>
          </template>
        </SectionCard>

        <SectionCard
          title="Danger zone"
          subtitle="Permanently remove your ScrapingAnt account and all related data."
        >
          <p class="profile-page__danger-copy">
            This action cannot be undone. Make sure you've cancelled any active
            paid subscriptions first.
          </p>
          <template #actions>
            <v-btn outlined color="error" @click="initiateRemoval"
              >Remove account</v-btn
            >
          </template>
        </SectionCard>
      </v-col>

      <!-- Right column -->
      <v-col cols="12" lg="6">
        <SectionCard
          v-if="hasAnyProxyCredentials"
          title="Proxy credentials"
          subtitle="Use these credentials to authenticate to the proxy gateway."
          class="mb-6"
        >
          <div
            v-if="
              residential_credentials &&
              residential_credentials.proxy_username
            "
            class="profile-page__proxy-block"
          >
            <div class="profile-page__proxy-head">
              <h4>Residential proxies</h4>
              <v-btn
                text
                small
                color="primary"
                class="profile-page__btn"
                @click="show_reset_residential_dialog = true"
              >
                <v-icon left small>mdi-refresh</v-icon>Reset
              </v-btn>
            </div>
            <CredentialField
              label="Username"
              :value="residential_credentials.proxy_username"
            />
            <CredentialField
              label="Password"
              :value="residential_credentials.proxy_secret || ''"
              secret
            />
          </div>

          <div
            v-if="
              residential_credentials &&
              residential_credentials.proxy_username &&
              datacenter_credentials &&
              datacenter_credentials.proxy_username
            "
            class="profile-page__divider"
          ></div>

          <div
            v-if="
              datacenter_credentials && datacenter_credentials.proxy_username
            "
            class="profile-page__proxy-block"
          >
            <div class="profile-page__proxy-head">
              <h4>Datacenter proxies</h4>
              <v-btn
                text
                small
                color="primary"
                class="profile-page__btn"
                @click="show_reset_datacenter_dialog = true"
              >
                <v-icon left small>mdi-refresh</v-icon>Reset
              </v-btn>
            </div>
            <CredentialField
              label="Username"
              :value="datacenter_credentials.proxy_username"
            />
            <CredentialField
              label="Password"
              :value="datacenter_credentials.proxy_secret || ''"
              secret
            />
          </div>
        </SectionCard>

        <SectionCard
          v-if="hasAnyPaymentUpdate"
          title="Payment details"
          subtitle="Update the card on file for any active subscription."
        >
          <ul class="profile-page__payments">
            <li
              v-if="current_subscription && current_subscription.update_url"
              @click="onUpdatePaymentDetailsClicked"
            >
              <span>Web Scraping API</span>
              <v-btn text small color="primary" class="profile-page__btn"
                >Update<v-icon right small>mdi-arrow-right</v-icon></v-btn
              >
            </li>
            <li
              v-if="
                current_residential_subscription &&
                current_residential_subscription.update_url
              "
              @click="onUpdateResidentialPaymentDetailsClicked"
            >
              <span>Residential proxies</span>
              <v-btn text small color="primary" class="profile-page__btn"
                >Update<v-icon right small>mdi-arrow-right</v-icon></v-btn
              >
            </li>
            <li
              v-if="
                current_datacenter_subscription &&
                current_datacenter_subscription.update_url
              "
              @click="onUpdateDatacenterPaymentDetailsClicked"
            >
              <span>Datacenter proxies</span>
              <v-btn text small color="primary" class="profile-page__btn"
                >Update<v-icon right small>mdi-arrow-right</v-icon></v-btn
              >
            </li>
          </ul>
        </SectionCard>
      </v-col>
    </v-row>

    <!-- Dialogs -->
    <ConfirmDialog
      :value="remove_dialog_active"
      title="Remove your account?"
      message="You will no longer be able to use ScrapingAnt without registering again. All user-related data will be permanently deleted."
      confirm-label="Remove account"
      confirm-color="error"
      cancel-label="Keep account"
      @confirm="onConfirmRemove"
      @input="remove_dialog_active = $event"
    />

    <ConfirmDialog
      :value="cancel_subscription_dialog_active"
      title="Active subscription detected"
      confirm-label="Open subscriptions"
      cancel-label="Close"
      :max-width="520"
      @confirm="goToSubscriptions"
      @input="cancel_subscription_dialog_active = $event"
    >
      <p>
        You have an active paid subscription. Please cancel it on the
        Subscriptions page before removing your account.
      </p>
    </ConfirmDialog>

    <ConfirmDialog
      :value="show_reset_residential_dialog"
      title="Reset residential credentials?"
      message="Your active residential credentials will be reset. All active sessions will be terminated."
      confirm-label="Reset"
      confirm-color="error"
      cancel-label="Cancel"
      @confirm="onResetResidentialClicked"
      @input="show_reset_residential_dialog = $event"
    />

    <ConfirmDialog
      :value="show_reset_datacenter_dialog"
      title="Reset datacenter credentials?"
      message="Your active datacenter credentials will be reset. All active sessions will be terminated. Changes will take effect within 15 minutes."
      confirm-label="Reset"
      confirm-color="error"
      cancel-label="Cancel"
      @confirm="onResetDatacenterClicked"
      @input="show_reset_datacenter_dialog = $event"
    />

    <div class="loading-overlay" v-if="loading">
      <v-progress-circular
        :size="50"
        :width="2"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>
  </div>
</template>

<script>
import { minLength, required, sameAs } from "vuelidate/lib/validators";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import CredentialField from "@/components/shared/CredentialField";
import ConfirmDialog from "@/components/shared/ConfirmDialog";

export default {
  name: "Profile",
  components: { PageHeader, SectionCard, CredentialField, ConfirmDialog },
  data() {
    return {
      api_token: "",
      residential_credentials: {},
      datacenter_credentials: {},
      showOldPassword: false,
      showNewPassword: false,
      showPasswordOneMoreTime: false,
      change_password: {
        old_password: "",
        new_password: "",
        new_password_repeated: "",
      },
      current_subscription: null,
      current_residential_subscription: null,
      current_datacenter_subscription: null,
      remove_dialog_active: false,
      cancel_subscription_dialog_active: false,
      show_reset_residential_dialog: false,
      show_reset_datacenter_dialog: false,
      loading: false,
    };
  },
  validations: {
    change_password: {
      old_password: { required },
      new_password: { required, minLength: minLength(6) },
      new_password_repeated: {
        required,
        sameAsPassword: sameAs("new_password"),
      },
    },
  },
  computed: {
    hasAnyProxyCredentials() {
      return (
        (this.residential_credentials &&
          this.residential_credentials.proxy_username) ||
        (this.datacenter_credentials &&
          this.datacenter_credentials.proxy_username)
      );
    },
    hasAnyPaymentUpdate() {
      return (
        (this.current_subscription && this.current_subscription.update_url) ||
        (this.current_residential_subscription &&
          this.current_residential_subscription.update_url) ||
        (this.current_datacenter_subscription &&
          this.current_datacenter_subscription.update_url)
      );
    },
    oldPasswordErrors() {
      const errors = [];
      if (!this.$v.change_password.old_password.$dirty) return errors;
      !this.$v.change_password.old_password.required &&
        errors.push("Please enter your password");
      return errors;
    },
    OneMoreTimePasswordErrors() {
      const errors = [];
      if (!this.$v.change_password.new_password_repeated.$dirty) return errors;
      !this.$v.change_password.new_password_repeated.required &&
        errors.push("Passwords do not match");
      !this.$v.change_password.new_password_repeated.sameAsPassword &&
        errors.push("Passwords do not match");
      return errors;
    },
    newPasswordErrors() {
      const errors = [];
      if (!this.$v.change_password.new_password.$dirty) return errors;
      !this.$v.change_password.new_password.required &&
        errors.push("Please enter a password with 6+ characters");
      !this.$v.change_password.new_password.minLength &&
        errors.push("Please enter a password with 6+ characters");
      return errors;
    },
    isChangePasswordDisabled() {
      return (
        this.$v.change_password.old_password.$error ||
        this.$v.change_password.new_password.$error ||
        this.$v.change_password.new_password_repeated.$error ||
        !this.$v.change_password.old_password.$dirty ||
        !this.$v.change_password.new_password.$dirty ||
        !this.$v.change_password.new_password_repeated.$dirty
      );
    },
  },
  methods: {
    regenerateApiToken() {
      this.loading = true;
      this.$store.dispatch("profile/generateAndGetNewApiToken").then(
        (res) => {
          this.api_token = res.data.token;
          this.loading = false;
          this.$notify({
            group: "main",
            text: "API token regenerated",
            duration: 1500,
          });
        },
        (err) => {
          console.log(err);
          this.loading = false;
          this.$notify({
            group: "main",
            classes: "main-notification error",
            text: "Failed to regenerate API token",
          });
        }
      );
    },
    initiateRemoval() {
      if (this.current_subscription && this.current_subscription.plan.price > 0) {
        this.cancel_subscription_dialog_active = true;
      } else {
        this.remove_dialog_active = true;
      }
    },
    goToSubscriptions() {
      this.cancel_subscription_dialog_active = false;
      this.$router.push({ name: "Subscriptions" });
    },
    onConfirmRemove() {
      this.remove_dialog_active = false;
      this.loading = true;
      this.$store.dispatch("profile/removeAccount").then(
        () => (this.loading = false),
        (err) => {
          console.log(err);
          this.loading = false;
          this.$notify({
            group: "main",
            classes: "main-notification error",
            text: "Failed to remove account. Please contact support.",
          });
        }
      );
    },
    getCurrentSubscription() {
      this.$store.dispatch("profile/getCurrentSubscription").then((res) => {
        this.current_subscription = res.data.subscriptions[0];
      }, console.log);
    },
    getCurrentResidentialSubscription() {
      this.$store
        .dispatch("profile/getCurrentResidentialSubscription")
        .then((res) => {
          this.current_residential_subscription = res;
        }, console.log);
    },
    getCurrentDatacenterSubscription() {
      this.$store
        .dispatch("profile/getCurrentDatacenterSubscription")
        .then((res) => {
          this.current_datacenter_subscription = res;
        }, console.log);
    },
    changeUserPassword() {
      this.loading = true;
      this.$store
        .dispatch("profile/changePassword", this.change_password)
        .then(
          () => {
            this.loading = false;
            this.$notify({
              group: "main",
              classes: "main-notification success",
              text: "Password changed successfully",
              duration: 2000,
            });
            this.$v.change_password.$reset();
            this.change_password.old_password = "";
            this.change_password.new_password = "";
            this.change_password.new_password_repeated = "";
          },
          (err) => {
            console.log(err);
            this.loading = false;
            this.$notify({
              group: "main",
              classes: "main-notification error",
              text: "Failed to change password. Please try again or contact support@scrapingant.com.",
              duration: 4000,
            });
          }
        );
    },
    onUpdatePaymentDetailsClicked() {
      // eslint-disable-next-line no-undef
      Paddle.Checkout.open({
        override: this.current_subscription.update_url,
        successCallback: () => {},
        closeCallback: () => {},
      });
    },
    onUpdateResidentialPaymentDetailsClicked() {
      // eslint-disable-next-line no-undef
      Paddle.Checkout.open({
        override: this.current_residential_subscription.update_url,
        successCallback: () => {},
        closeCallback: () => {},
      });
    },
    onUpdateDatacenterPaymentDetailsClicked() {
      // eslint-disable-next-line no-undef
      Paddle.Checkout.open({
        override: this.current_datacenter_subscription.update_url,
        successCallback: () => {},
        closeCallback: () => {},
      });
    },
    getResidentialCredentials() {
      return this.$store.dispatch("profile/getResidentialCredentials").then(
        (res) => {
          this.residential_credentials = res;
          this.loading = false;
        },
        (err) => {
          console.log(err);
          this.loading = false;
        }
      );
    },
    getDatacenterCredentials() {
      return this.$store.dispatch("profile/getDatacenterCredentials").then(
        (res) => {
          this.datacenter_credentials = res;
          this.loading = false;
        },
        (err) => {
          console.log(err);
          this.loading = false;
        }
      );
    },
    async onResetResidentialClicked() {
      this.show_reset_residential_dialog = false;
      try {
        this.loading = true;
        await this.$store.dispatch("profile/resetResidentialCredentials");
        await new Promise((r) => setTimeout(r, 2000));
        await this.getResidentialCredentials();
        this.$notify({
          group: "main",
          classes: "main-notification success",
          text: "Residential credentials reset",
        });
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },
    async onResetDatacenterClicked() {
      this.show_reset_datacenter_dialog = false;
      try {
        this.loading = true;
        await this.$store.dispatch("profile/resetDatacenterCredentials");
        await new Promise((r) => setTimeout(r, 2000));
        await this.getDatacenterCredentials();
        this.$notify({
          group: "main",
          classes: "main-notification success",
          text: "Datacenter credentials reset",
        });
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.loading = true;
    this.getCurrentSubscription();
    this.getCurrentResidentialSubscription();
    this.getCurrentDatacenterSubscription();
    this.getResidentialCredentials();
    this.getDatacenterCredentials();
    this.$store.dispatch("profile/getApiToken").then(
      (res) => {
        this.api_token = res.data.token;
        this.loading = false;
      },
      (err) => {
        console.log(err);
        this.loading = false;
      }
    );
  },
};
</script>

<style scoped lang="scss">
.profile-page {
  position: relative;

  &__regen {
    margin-top: 10px;
  }

  &__btn {
    text-transform: none !important;
    letter-spacing: 0 !important;
    font-weight: 500 !important;
  }

  &__divider {
    height: 1px;
    background-color: rgba(15, 23, 42, 0.08);
    margin: 22px 0;
  }

  &__subhead {
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
    margin: 0 0 14px;
  }

  &__danger-copy {
    font-size: 13px;
    color: rgba(15, 23, 42, 0.62);
    line-height: 1.55;
    margin: 0;
  }

  &__proxy-block {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__proxy-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    h4 {
      font-size: 14px;
      font-weight: 600;
      color: #0f172a;
      margin: 0;
    }
  }

  &__payments {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 12px 0;
      border-bottom: 1px solid rgba(15, 23, 42, 0.06);
      cursor: pointer;
      font-size: 13px;
      color: #0f172a;
      transition: padding-left 120ms ease;

      &:last-child {
        border-bottom: none;
      }

      &:hover {
        padding-left: 4px;
      }

      span {
        font-weight: 500;
      }
    }
  }
}
</style>
